<script lang="ts">
  import InlineSVG from 'svelte-inline-svg';

  import handleClickOutside from '../../utils/handle-click-outside';
  import {getLocaleDateFormatPattern} from '../../utils/date-utils';

  import clearIcon from '../../assets/icons/cross.svg';

  import IconButton from '../icon-button/IconButton.svelte';
  import Calendar from '../calendar/Calendar.svelte';

  export let type: 'date' | 'month' = 'date';
  export let label: string | null = null;
  export let placeholder =
    (type === 'date' && getLocaleDateFormatPattern()) ||
    (type === 'month' &&
      getLocaleDateFormatPattern(undefined, {
        month: 'long',
        year: 'numeric'
      })) ||
    '';
  export let value: Date | null = null;
  export let min: Date | null = null;
  export let max: Date | null = null;
  export let id: string | null = null;
  export let required = false;
  export let closeOnSelect = true;

  let showDatePicker = false;

  $: formattedValue = (() => {
    if (value) {
      if (type === 'date') {
        return value.toLocaleDateString();
      }

      if (type === 'month') {
        return value.toLocaleDateString(undefined, {
          month: 'long',
          year: 'numeric'
        });
      }
    }

    return '';
  })();

  // Reset the input value and close the date picker
  const clearInput = () => {
    // Timeout needed to not remove the target element before
    // event listeners could work with this target element.
    // Removing this timeout will for example cause that
    // the filters dropdown is closed immediately.
    setTimeout(() => {
      showDatePicker = false;
      value = null;
    });
  };
</script>

<style lang="postcss">
  .container {
    display: grid;
    gap: 0.25rem;
    padding: 0.25rem 0;
  }

  .inputGroup {
    position: relative;
  }

  .inputGroup input {
    width: 100%;
    caret-color: transparent;
  }

  .inputGroup input + :global(.inputClearButton) {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    margin: 0.25rem;
  }

  :global(.datePickerCalendar) {
    z-index: 10;
    position: absolute;
    top: 100%;
    left: 0;
  }
</style>

<div class="container {$$props.class}">
  <div class="label">
    {#if label}
      <label for={id}>{label}{required ? '*' : ''}</label>
    {/if}
  </div>
  <div
    class="inputGroup {$$props.inputWrapperClass}"
    use:handleClickOutside
    on:click_outside={() => {
      showDatePicker = false;
    }}>
    <!-- prevent default on input keydown and hide caret to simulate a readonly field without losing the browser's native form validation -->
    <input
      type="text"
      autocomplete="off"
      {required}
      {placeholder}
      name={id}
      {id}
      value={formattedValue}
      on:focus={() => {
        showDatePicker = true;
      }}
      on:keydown|preventDefault />

    {#if formattedValue}
      <IconButton class="inputClearButton" on:click={clearInput}>
        <InlineSVG src={clearIcon} />
      </IconButton>
    {/if}

    {#if showDatePicker}
      <Calendar
        class="datePickerCalendar"
        {type}
        selectedDate={value}
        minDate={min}
        maxDate={max}
        on:change={({detail: newDate}) => {
          if (closeOnSelect) {
            showDatePicker = false;
          }
          value = newDate;
        }} />
    {/if}
  </div>
</div>
